import React, { useEffect, useRef, useState } from 'react'
import { Button, Drawer, Icon, Spin, message as antMessage } from 'antd'

import FinalizedReportModal from './FinalizedReportModal'
import { MAX_REFERENCE_FILE_SIZE } from './constants'
import {
  ConversationCard,
  MessageList,
  PhaseWorkspace,
  ProgressPreview,
  VisionDirectionCard,
  VisionSummaryCard,
  WorkflowStepDivider
} from './components'
import {
  getFriendlyErrorMessage,
  getPhaseIndex,
  getPhaseView,
  getReferenceKind,
  isAcceptedReferenceFile,
  revokePendingReferencePreviews
} from './utils'
import './CreativeDirectionChat.scss'

const getMessagePhase = message => message?.phase || 'vision'

const CreativeDirectionChat = ({
  sessionId,
  messages,
  metadata,
  phaseSteps,
  phaseData,
  creativeGuideline,
  progressPercent,
  creativeStatement,
  acceptedExtensions,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  referenceUploadProgress,
  referenceUploadStatus,
  referenceUploadMessage,
  generationProgress,
  generationStatus,
  generationMessage,
  isLockingVision,
  isVisionLocked,
  isFinalized,
  phaseBusyAction,
  error,
  onStopAgentTurn,
  onSendMessage,
  onUploadRequestedReference,
  onSynthesizeVision,
  onLockVision,
  onExtractPhase,
  onVerifyEntity,
  onVerifyAllPhase,
  onGenerateStoryboardPlan,
  onRefreshGenerationStatus,
  onAdvancePhase,
  onFinalizeCreativeDirection,
  onStartPrompt,
  onRetryCreateSession,
  onSkipToShotList
}) => {
  const [draftMessage, setDraftMessage] = useState('')
  const [pendingReferences, setPendingReferences] = useState([])
  const [isFinalizedReportOpen, setIsFinalizedReportOpen] = useState(false)
  const [detailDrawerPhase, setDetailDrawerPhase] = useState('')
  const scrollRef = useRef(null)
  const pendingReferencesRef = useRef([])
  const currentPhase = metadata.currentPhase || 'vision'
  const isVisionPhase = currentPhase === 'vision'

  const togglePhaseDetails = phase => {
    if (phase === 'finalized' && isFinalized && creativeGuideline) {
      setDetailDrawerPhase('')
      setIsFinalizedReportOpen(true)
      return
    }

    setDetailDrawerPhase(prevPhase => (prevPhase === phase ? '' : phase))
  }

  useEffect(() => {
    pendingReferencesRef.current = pendingReferences
  }, [pendingReferences])

  useEffect(
    () => () => {
      revokePendingReferencePreviews(pendingReferencesRef.current)
    },
    []
  )

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        const scrollContainer = scrollRef.current.closest(
          '.video-generation--creative-direction'
        )

        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight
          return
        }

        scrollRef.current.scrollIntoView({
          block: 'end',
          behavior: 'smooth'
        })
      }
    }

    const frameId = window.requestAnimationFrame(scrollToBottom)
    const timeoutId = window.setTimeout(scrollToBottom, 120)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.clearTimeout(timeoutId)
    }
  }, [
    creativeStatement,
    currentPhase,
    isStreaming,
    isSynthesizingVision,
    messages,
    phaseData,
    phaseBusyAction,
    error
  ])

  const handleAttachReference = file => {
    if (!file) return false

    if (!isAcceptedReferenceFile(file, acceptedExtensions)) {
      antMessage.error('Attach an image or video reference file.')
      return false
    }

    if (file.size > MAX_REFERENCE_FILE_SIZE) {
      antMessage.error('Reference files must be 50 MB or less.')
      return false
    }

    const fileKey =
      file.uid || `${file.name}-${file.size}-${file.lastModified || Date.now()}`
    const previewUrl =
      getReferenceKind(file) === 'image' && window.URL?.createObjectURL
        ? window.URL.createObjectURL(file)
        : ''

    setPendingReferences(prev => {
      if (prev.some(item => item.fileKey === fileKey)) {
        if (previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(previewUrl)
        }
        return prev
      }

      return [
        ...prev,
        {
          id: `reference-${fileKey}`,
          fileKey,
          file,
          previewUrl
        }
      ]
    })

    return false
  }

  const handleRemovePendingReference = referenceId => {
    setPendingReferences(prev =>
      prev.filter(item => {
        const shouldKeep = item.id !== referenceId
        if (!shouldKeep && item.previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(item.previewUrl)
        }
        return shouldKeep
      })
    )
  }

  const handleSubmit = async () => {
    const message = draftMessage.trim()
    if (
      !sessionId ||
      !message ||
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isUploadingReference ||
      isLockingVision ||
      Boolean(phaseBusyAction)
    ) {
      return
    }

    const referencesToUpload = pendingReferences

    setDraftMessage('')

    if (referencesToUpload.length) {
      const uploadedReferenceIds = referencesToUpload.map(item => item.id)
      setPendingReferences(prev =>
        prev.filter(item => !uploadedReferenceIds.includes(item.id))
      )
      revokePendingReferencePreviews(referencesToUpload)
    }

    await onSendMessage(message, {
      files: referencesToUpload.map(item => item.file)
    })
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleStarterPrompt = item => {
    if (
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isLockingVision ||
      Boolean(phaseBusyAction) ||
      !sessionId
    ) {
      return
    }

    if (item.action === 'start') {
      onStartPrompt()
      return
    }

    onSendMessage(item.message)
  }

  if (isCreatingSession) {
    return (
      <div className="creative-direction-chat__loading">
        <Spin size="large" />
        <div>
          <h3>Preparing AI</h3>
          <p>Starting a creative direction session for this story.</p>
        </div>
      </div>
    )
  }

  if (!sessionId && error) {
    return (
      <div className="creative-direction-chat__empty">
        <Icon type="warning" />
        <h3>Creative direction could not start</h3>
        <p>{getFriendlyErrorMessage(error)}</p>
        <Button type="primary" onClick={onRetryCreateSession}>
          Try again
        </Button>
      </div>
    )
  }

  const currentPhaseIndex = getPhaseIndex(phaseSteps, currentPhase)
  const visibleWorkflowPhases = phaseSteps.slice(1, currentPhaseIndex + 1)
  const hasConversation = messages.some(item =>
    String(item.content || '').trim()
  )
  const hasStreamingMessage = messages.some(item => item.streaming)
  const getMessagesForPhase = phase =>
    messages.filter(item => getMessagePhase(item) === phase)
  const shouldShowTypingForPhase = phase =>
    phase === currentPhase && isStreaming && !hasStreamingMessage
  const shouldShowVisionTimeline =
    hasConversation || Boolean(creativeStatement) || isSynthesizingVision
  const isChatTurnBusy =
    isStreaming ||
    isSynthesizingVision ||
    isUploadingReference ||
    isLockingVision
  const previewExpandedPhases = detailDrawerPhase
    ? { [detailDrawerPhase]: true }
    : {}

  const renderPhaseWorkspace = ({
    phaseKey,
    isPhaseActive,
    isPhaseExpanded,
    showPhaseActions,
    isDrawerView = false
  }) => (
    <PhaseWorkspace
      currentPhase={phaseKey}
      phaseSteps={phaseSteps}
      phaseData={phaseData}
      creativeGuideline={creativeGuideline}
      isFinalized={isFinalized}
      creativeStatement={creativeStatement}
      hasConversation={hasConversation}
      isStreaming={isStreaming}
      isSynthesizingVision={isSynthesizingVision}
      isLockingVision={isLockingVision}
      isVisionLocked={isVisionLocked}
      isActivePhase={isPhaseActive}
      isSelectedPhase={isPhaseExpanded}
      showActions={showPhaseActions}
      phaseBusyAction={phaseBusyAction}
      acceptedExtensions={acceptedExtensions}
      referenceUploadProgress={referenceUploadProgress}
      referenceUploadStatus={referenceUploadStatus}
      referenceUploadMessage={referenceUploadMessage}
      generationProgress={generationProgress}
      generationStatus={generationStatus}
      generationMessage={generationMessage}
      onExtractPhase={onExtractPhase}
      onVerifyEntity={onVerifyEntity}
      onVerifyAllPhase={onVerifyAllPhase}
      onGenerateStoryboardPlan={onGenerateStoryboardPlan}
      onRefreshGenerationStatus={onRefreshGenerationStatus}
      onUploadRequestedReference={onUploadRequestedReference}
      onAdvancePhase={onAdvancePhase}
      onSynthesizeVision={onSynthesizeVision}
      onLockVision={onLockVision}
      onFinalizeCreativeDirection={onFinalizeCreativeDirection}
      onViewFinalizedReport={() => setIsFinalizedReportOpen(true)}
      onTogglePhase={() => togglePhaseDetails(phaseKey)}
      isDrawerView={isDrawerView}
    />
  )

  const activePhaseWorkspace =
    isVisionPhase && !isVisionLocked ? null : isChatTurnBusy ? (
      <div className="creative-direction-chat__phase-busy-strip">
        <Icon type="down" />
        <span>{getPhaseView(currentPhase).label}</span>
      </div>
    ) : isVisionPhase ? (
      <VisionSummaryCard
        creativeStatement={creativeStatement}
        isVisionLocked={isVisionLocked}
        onTogglePhase={() => togglePhaseDetails('vision')}
      />
    ) : (
      renderPhaseWorkspace({
        phaseKey: currentPhase,
        isPhaseActive: true,
        isPhaseExpanded: false,
        showPhaseActions: true
      })
    )

  const drawerPhaseView = detailDrawerPhase
    ? getPhaseView(detailDrawerPhase)
    : null

  const timelinePanel =
    isVisionPhase && !shouldShowVisionTimeline ? null : isVisionPhase ? (
      <>
        <MessageList
          messages={getMessagesForPhase('vision')}
          showTyping={shouldShowTypingForPhase('vision')}
        />
        <VisionDirectionCard
          creativeStatement={creativeStatement}
          hasConversation={hasConversation}
          isStreaming={isStreaming}
          isSynthesizingVision={isSynthesizingVision}
          isLockingVision={isLockingVision}
          isVisionLocked={isVisionLocked}
          isArchived={false}
          onSynthesizeVision={onSynthesizeVision}
          onLockVision={onLockVision}
        />
      </>
    ) : (
      <>
        <VisionSummaryCard
          creativeStatement={creativeStatement}
          isVisionLocked={isVisionLocked}
          onTogglePhase={() => togglePhaseDetails('vision')}
        />
        {visibleWorkflowPhases.map((phaseStep, index) => {
          const phaseKey = phaseStep.key
          const isPhaseActive = phaseKey === currentPhase

          return (
            <React.Fragment key={phaseKey}>
              <WorkflowStepDivider
                stepNumber={index + 2}
                label={getPhaseView(phaseKey).label}
              />
              <MessageList
                messages={getMessagesForPhase(phaseKey)}
                showTyping={shouldShowTypingForPhase(phaseKey)}
              />
              {isPhaseActive
                ? null
                : renderPhaseWorkspace({
                    phaseKey,
                    isPhaseActive,
                    isPhaseExpanded: false,
                    showPhaseActions: false
                  })}
            </React.Fragment>
          )
        })}
      </>
    )

  return (
    <div className="creative-direction-chat">
      <ProgressPreview
        phaseSteps={phaseSteps}
        currentPhase={currentPhase}
        expandedPhases={previewExpandedPhases}
        progressPercent={progressPercent}
        isFinalized={isFinalized}
        onPhaseSelect={togglePhaseDetails}
      />

      <main className="creative-direction-chat__main">
        <ConversationCard
          sessionId={sessionId}
          messages={messages}
          timelinePanel={timelinePanel}
          scrollRef={scrollRef}
          errorMessage={
            error && sessionId ? getFriendlyErrorMessage(error) : ''
          }
          isCreatingSession={isCreatingSession}
          isStreaming={isStreaming}
          isSynthesizingVision={isSynthesizingVision}
          isUploadingReference={isUploadingReference}
          isLockingVision={isLockingVision}
          phaseBusyAction={phaseBusyAction}
          acceptedExtensions={acceptedExtensions}
          pendingReferences={pendingReferences}
          activeWorkspace={activePhaseWorkspace}
          draftMessage={draftMessage}
          onDraftMessageChange={setDraftMessage}
          onAttachReference={handleAttachReference}
          onRemovePendingReference={handleRemovePendingReference}
          onSubmit={handleSubmit}
          onStopAgentTurn={onStopAgentTurn}
          onKeyDown={handleKeyDown}
          onStarterPrompt={handleStarterPrompt}
          onSkipToShotList={onSkipToShotList}
          isSkipDisabled={
            isCreatingSession ||
            isStreaming ||
            isSynthesizingVision ||
            isUploadingReference ||
            isLockingVision ||
            Boolean(phaseBusyAction)
          }
        />
      </main>

      <FinalizedReportModal
        visible={isFinalizedReportOpen}
        report={creativeGuideline}
        onClose={() => setIsFinalizedReportOpen(false)}
      />

      <Drawer
        className="creative-direction-chat__phase-drawer"
        title={drawerPhaseView ? `${drawerPhaseView.label} details` : ''}
        placement="right"
        width="60vw"
        destroyOnClose
        visible={Boolean(detailDrawerPhase)}
        onClose={() => setDetailDrawerPhase('')}
      >
        {detailDrawerPhase ? (
          <div className="creative-direction-chat__phase-drawer-body">
            {renderPhaseWorkspace({
              phaseKey: detailDrawerPhase,
              isPhaseActive: detailDrawerPhase === currentPhase,
              isPhaseExpanded: true,
              showPhaseActions:
                detailDrawerPhase === currentPhase ||
                detailDrawerPhase === 'storyboard',
              isDrawerView: true
            })}
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}

export default CreativeDirectionChat
